/**
 * Agent Names Utility
 * 
 * This file provides a centralized way to manage agent names based on
 * tasks and sections. This approach ensures consistent agent naming
 * across the application.
 */

// Section IDs for reference - these match the database IDs
const SECTION_IDS = {
  FUNCTIONAL_ANALYSIS: '68096c7f0ed65d2770008725',
  DESIGN: '68096c7f0ed65d2770008727',
  CODING_DEVELOPMENT: '68096c7f0ed65d2770008729', 
  TESTING: '6812086a4afcec422266f72c',
  INFRASTRUCTURE: '6812086a4afcec422266f72e',
};

// Default agent names by section
const SECTION_AGENTS = {
  // Functional Analysis section
  [SECTION_IDS.FUNCTIONAL_ANALYSIS]: {
    DEFAULT: { name: 'Felix', role: 'Analysis' },
    'user-roles': { name: 'Rolie', role: 'User Roles' },
    'user-stories': { name: 'Storia', role: 'User Stories' },
    'features': { name: 'Featy', role: 'Features' },
    'project-info': { name: 'Proja', role: 'Project Info' },
    'process-flows': { name: 'Flo', role: 'Process Flows' },
    'knowledge-hub-analysis': { name: 'Felix', role: 'Analysis' },
  },

  // Design section
  [SECTION_IDS.DESIGN]: {
    DEFAULT: { name: 'Diana', role: 'UI/UX' },
    'wireframes': { name: 'Wiro', role: 'UI/UX Wireframes' },
    'design-system': { name: 'Stylia', role: 'UI/UX Style Guide' },
    'design-guidelines': { name: 'Guy', role: 'UI/UX Guidelines' },
    'designs': { name: 'Layo', role: 'UI/UX Layout' },
    'design-references': { name: 'Refa', role: 'UI/UX References' },
    'knowledge-hub-design': { name: 'Diana', role: 'UI/UX' },
  },

  // Technical section
  [SECTION_IDS.CODING_DEVELOPMENT]: {
    DEFAULT: { name: 'Devon', role: 'Development' },
    'architecture': { name: 'Archie', role: 'Architecture' },
    'api-guidelines': { name: 'Alex', role: 'API' },
    'technology-stack': { name: 'Stacky', role: 'Technology Stack' },
    'database': { name: 'Dora', role: 'Database' },
    'dev-guidelines': { name: 'Cody', role: 'Coding Guidelines' },
    'security-guidelines': { name: 'Secury', role: 'Security Guidelines' },
    'user-permissions': { name: 'Missy', role: 'User Permissions' },
    'field-validations': { name: 'Fieldy', role: 'Field Validations' },
    'knowledge-hub-coding': { name: 'Devon', role: 'Development' },
  },

  // Testing section
  [SECTION_IDS.TESTING]: {
    DEFAULT: { name: 'Tessa', role: 'Testing' },
    'test-cases': { name: 'Funcy', role: 'Functional Testing' },
    'automated-testing': { name: 'Auton', role: 'Automated Testing' },
    'security-testing': { name: 'Secu', role: 'Security Testing' },
    'performance-testing': { name: 'Perfie', role: 'Performance Testing' },
    'field-validation': { name: 'Fieldy', role: 'Field Validation' },
    'knowledge-hub-testing': { name: 'Tessa', role: 'Testing' },
  },

  // Infrastructure section
  [SECTION_IDS.INFRASTRUCTURE]: {
    DEFAULT: { name: 'Ian', role: 'Infrastructure' },
    'version-control': { name: 'Versio', role: 'Code Version Control' },
    'cicd-pipeline': { name: 'Liny', role: 'CI/CD Pipeline' },
    'deployment': { name: 'Deplo', role: 'Deployment' },
    'knowledge-hub-infrastructure': { name: 'Ian', role: 'Infrastructure' },
  },

  // Default for any section
  DEFAULT: { name: 'Ailyce', role: 'AI Agent' }
};

// Task ID mapping - map task IDs to their task keys for lookup
const TASK_ID_MAP = {
  // Will be populated from database or configuration
};

// Fallback mapping by task name (case insensitive)
const TASK_NAME_MAP = {
  // Direct task name mappings
  'project info': 'project-info',
  'features': 'features',
  'user roles': 'user-roles',
  'user stories': 'user-stories',
  'process flows': 'process-flows',
  'knowledge hub analysis': 'knowledge-hub-analysis',

  'ui/ux references': 'design-references',
  'ui/ux wireframes': 'wireframes',
  'ui/ux guidelines': 'design-guidelines',
  'ui/ux style guide': 'design-system',
  'ui/ux layout': 'designs',
  'knowledge hub ui/ux': 'knowledge-hub-design',

  'field validations': 'field-validations',
  'field validation': 'field-validation',
  'technology stack': 'technology-stack',
  'architecture': 'architecture',
  'development guidelines': 'dev-guidelines',
  'security guidelines': 'security-guidelines',
  'user permissions': 'user-permissions',
  'coding guidelines': 'dev-guidelines',
  'knowledge hub coding': 'knowledge-hub-coding',

  'functional testing': 'test-cases',
  'automation testing': 'automated-testing',
  'security testing': 'security-testing',
  'performance testing': 'performance-testing',
  'knowledge hub quality': 'knowledge-hub-testing',

  'code version control': 'version-control',
  'ci/cd pipeline': 'cicd-pipeline',
  'deployment': 'deployment',
  'knowledge hub infrastructure': 'knowledge-hub-infrastructure',

  // Additional mappings to handle task IDs
  'extract-prd': 'project-info',
  'chat-prd': 'project-info',
  'field-validations': 'field-validations',
  'tech-stack': 'technology-stack',
  'technology-stack': 'technology-stack',
  'dev-guidelines': 'dev-guidelines',
  'user-roles': 'user-roles',
  'user-stories': 'user-stories',
  'security-guidelines': 'security-guidelines',
  'api-guidelines': 'api-guidelines',
  'process-flows': 'process-flows',
  'flow': 'process-flows',
  'wireframes': 'wireframes',
  'design-system': 'design-system',
  'design-guidelines': 'design-guidelines',
  'designs': 'designs',
  'user-permissions': 'user-permissions',
  'test-cases': 'test-cases',
  'automation-testing': 'automated-testing',
  'security-testing': 'security-testing',
  'performance-testing': 'performance-testing',
  'version-control': 'version-control',
  'cicd-pipeline': 'cicd-pipeline',
};

/**
 * Get agent information for a task
 * @param {Object} task - The task object with section reference
 * @returns {Object} The agent information with name and role
 */
export const getAgentForTask = (task) => {
  if (!task) return SECTION_AGENTS.DEFAULT;

  console.log("getAgentForTask input:", task);

  // Try to get the section ID
  // Check different possible paths to find the section ID
  const sectionId =
    // Check if sectionId is directly on the task
    task.sectionId ||
    // Check if section._id exists
    (task.section && task.section._id ? task.section._id.toString() :
    // Check if section is directly the ID string
    (typeof task.section === 'string' ? task.section : null));

  console.log("Looking for section ID:", sectionId);

  // Try all available methods for finding the task key
  let taskKey = null;

  // Method 1: Try to map the task ID directly
  if (task.id) {
    console.log("Trying to map task ID:", task.id);
    // If the task ID is directly in our map
    if (TASK_NAME_MAP[task.id]) {
      taskKey = TASK_NAME_MAP[task.id];
      console.log("Found task key from task.id:", taskKey);
    }
  }

  // Method 2: Try by task._id if available
  if (!taskKey && task._id) {
    console.log("Trying to map task._id:", task._id.toString());
    if (TASK_ID_MAP[task._id.toString()]) {
      taskKey = TASK_ID_MAP[task._id.toString()];
      console.log("Found task key from task._id:", taskKey);
    }
  }

  // Method 3: Try by task title
  if (!taskKey && task.title) {
    console.log("Trying to map task title:", task.title);
    const normalizedTitle = task.title.toLowerCase().trim();
    if (TASK_NAME_MAP[normalizedTitle]) {
      taskKey = TASK_NAME_MAP[normalizedTitle];
      console.log("Found task key from normalized title:", taskKey);
    }
  }

  // If we have a valid section ID and task key
  if (sectionId && SECTION_AGENTS[sectionId] && taskKey && SECTION_AGENTS[sectionId][taskKey]) {
    console.log(`Found agent for section ${sectionId}, task ${taskKey}:`, SECTION_AGENTS[sectionId][taskKey]);
    return SECTION_AGENTS[sectionId][taskKey];
  }

  // Fallback: Use default agent for the section
  if (sectionId && SECTION_AGENTS[sectionId] && SECTION_AGENTS[sectionId].DEFAULT) {
    console.log(`Using default agent for section ${sectionId}:`, SECTION_AGENTS[sectionId].DEFAULT);
    return SECTION_AGENTS[sectionId].DEFAULT;
  }

  // Last resort: Use the global default agent
  console.log("Using global default agent:", SECTION_AGENTS.DEFAULT);
  return SECTION_AGENTS.DEFAULT;
};

/**
 * Get agent information for a section
 * @param {Object|string} section - The section object or ID
 * @returns {Object} The agent information with name and role
 */
export const getDefaultAgentForSection = (section) => {
  if (!section) return SECTION_AGENTS.DEFAULT;
  
  // Extract section ID
  const sectionId = typeof section === 'object' && section._id 
    ? section._id.toString() 
    : section;
  
  // Return the section's default agent or the global default
  return SECTION_AGENTS[sectionId]?.DEFAULT || SECTION_AGENTS.DEFAULT;
};

/**
 * Get all agents for a section
 * @param {Object|string} section - The section object or ID
 * @returns {Object} Map of task keys to agent information
 */
export const getAllAgentsForSection = (section) => {
  if (!section) return { DEFAULT: SECTION_AGENTS.DEFAULT };
  
  // Extract section ID
  const sectionId = typeof section === 'object' && section._id 
    ? section._id.toString() 
    : section;
  
  // Return all agents for the section or just the default
  return SECTION_AGENTS[sectionId] || { DEFAULT: SECTION_AGENTS.DEFAULT };
};

/**
 * Register a task ID for agent mapping
 * @param {string} taskId - The task ID
 * @param {string} taskKey - The task key for lookup in SECTION_AGENTS
 */
export const registerTaskId = (taskId, taskKey) => {
  if (taskId && taskKey) {
    TASK_ID_MAP[taskId] = taskKey;
  }
};

/**
 * Initialize task ID mapping from existing tasks
 * @param {Array} tasks - Array of task objects with IDs and titles
 */
export const initializeTaskIdMap = (tasks) => {
  if (!Array.isArray(tasks)) return;
  
  tasks.forEach(task => {
    if (!task._id || !task.title) return;
    
    const normalizedTitle = task.title.toLowerCase().trim();
    const taskKey = TASK_NAME_MAP[normalizedTitle];
    
    if (taskKey) {
      TASK_ID_MAP[task._id.toString()] = taskKey;
    }
  });
};